import { Transaction } from '@starsoft/common/models';
import { useMemo } from 'react';
import { Edge, Node } from 'reactflow';
import { NodeType } from './props';
import { TransactionsCommonMappingModalContentBoardNode } from '@starsoft/common/components/Transactions/Common/Mapping/Modal/Content/Board/Node';

const NODE_WIDTH = 350; // Constant for node width
const NODE_VERTICAL_SPACE = 100; // Constant for vertical space between nodes

/**
 * Custom hook to compose nodes and edges from a transaction.
 * This hook generates nodes and edges for a graph representation of transaction transfers.
 *
 * @param {Transaction} [transaction] - The transaction object containing transfer data.
 * @returns {Object} - An object containing arrays of nodes and edges.
 *
 * Example usage:
 * const { nodes, edges } = useComposedNodesFromTransaction(transaction);
 */
export function useComposedNodesFromTransaction(transaction?: Transaction): {
  nodes: Array<Node>;
  edges: Array<Edge>;
} {
  // Memoized computation of nodes based on transaction data
  const nodes = useMemo(() => {
    return (
      transaction?.transfers?.flatMap((transfer, rowIndex) =>
        Object.keys(NodeType).map((type, colIndex) => {
          const nodeId = `node-transfer-${transfer?.id}-${type}`; // Unique node ID
          return {
            id: nodeId,
            data: {
              label: (
                <TransactionsCommonMappingModalContentBoardNode
                  transfer={transfer}
                  type={type as NodeType}
                />
              ),
            },
            position: {
              x: NODE_WIDTH * colIndex, // Horizontal position based on column index
              y: NODE_VERTICAL_SPACE * rowIndex, // Vertical position based on row index
            },
            sourcePosition: 'right', // Source position for edges
            targetPosition: 'left', // Target position for edges
          } as Node;
        }),
      ) ?? []
    )?.flat();
  }, [transaction]);

  // Memoized computation of edges based on transaction data
  const edges = useMemo(() => {
    const edgesList: Array<Edge> = [];

    transaction?.transfers?.forEach((transfer, rowIndex) => {
      const nodeIds = Object.keys(NodeType).map(
        type => `node-transfer-${transfer?.id}-${type}`,
      );

      for (let i = 0; i < nodeIds.length - 1; i++) {
        edgesList.push({
          id: `e-${nodeIds[i]}-${nodeIds[i + 1]}`, // Unique edge ID
          source: nodeIds[i], // Source node ID
          target: nodeIds[i + 1], // Target node ID
        });
      }
    });

    return edgesList;
  }, [transaction]);

  // Return empty arrays if no transaction is provided
  if (!transaction) {
    return {
      nodes: [],
      edges: [],
    };
  }

  // Return the computed nodes and edges
  return {
    nodes,
    edges,
  };
}
